import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";

interface ContextSeller {
  code: string;
  name: string;
  seller_id: number;
}

interface SelectedStoresContextValue {
  selectedStores: ContextSeller[];
  addStore: (store: ContextSeller) => void;
  removeStore: (storeId: number) => void;
  clearStores: () => void;
}

const SelectedStoresContext = createContext<
  SelectedStoresContextValue | undefined
>(undefined);

export const SelectedStoresProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [selectedStores, setSelectedStores] = useState<ContextSeller[]>(() => {
    return JSON.parse(localStorage.getItem("selectedStores") || "[]");
  });

  useEffect(() => {
    localStorage.setItem("selectedStores", JSON.stringify(selectedStores));
  }, [selectedStores]);

  const addStore = (store: ContextSeller) => {
    setSelectedStores((prev) => {
      if (prev.some((s) => s.seller_id === store.seller_id)) return prev;
      return [...prev, store];
    });
  };

  const removeStore = (storeId: number) => {
    setSelectedStores((prev) =>
      prev.filter((store) => store.seller_id !== storeId),
    );
  };

  const clearStores = () => {
    setSelectedStores([]);
  };

  return (
    <SelectedStoresContext.Provider
      value={{ selectedStores, addStore, removeStore, clearStores }}
    >
      {children}
    </SelectedStoresContext.Provider>
  );
};

export const useSelectedStores = (): SelectedStoresContextValue => {
  const context = useContext(SelectedStoresContext);
  if (!context) {
    throw new Error(
      "useSelectedStores must be used within a SelectedStoresProvider",
    );
  }
  return context;
};
